import React, { useContext, useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { appContext } from 'common/contexts';
import { Routes as R } from 'common/enums';
import { InventoryItem, StockMovement } from 'common/types';
import { ArrowLeftTwoTone } from 'common/icons';
import styles from './stockMovementEditor.module.css';

const StockMovementEditor: React.FC = () => {
  const history = useHistory();
  const { state, actions, helpers } = useContext(appContext);
  const { inventoryItems = [] } = state;
  const { translation } = helpers;
  const { t } = translation;

  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);

  // Form state
  const [formData, setFormData] = useState<Omit<StockMovement, 'id' | 'date' | 'ownerId' | 'parentUserId'>>({
    inventoryItemId: '',
    type: 'purchase',
    quantity: 0,
    notes: '',
    reference: '',
    costPrice: 0
  });

  // Filter out deleted items
  const activeItems = inventoryItems.filter(item => !item.isDeleted);

  // Update selected item when inventoryItemId changes
  useEffect(() => {
    if (formData.inventoryItemId) {
      const item = inventoryItems.find(item => item.id === formData.inventoryItemId);
      setSelectedItem(item || null);

      // If it's a purchase, set the cost price to the item's cost price as default
      if (item && formData.type === 'purchase') {
        setFormData(prev => ({
          ...prev,
          costPrice: item.costPrice
        }));
      }
    } else {
      setSelectedItem(null);
    }
  }, [formData.inventoryItemId, formData.type, inventoryItems]);

  // Update quantity sign based on movement type
  useEffect(() => {
    if (formData.type === 'purchase') {
      // Ensure quantity is positive for purchases
      if (formData.quantity < 0) {
        setFormData(prev => ({
          ...prev,
          quantity: Math.abs(prev.quantity)
        }));
      }
    } else if (formData.type === 'consumption' || formData.type === 'sale') {
      // Ensure quantity is negative for consumption and sales
      if (formData.quantity > 0) {
        setFormData(prev => ({
          ...prev,
          quantity: -Math.abs(prev.quantity)
        }));
      }
    }
  }, [formData.type, formData.quantity]);

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.inventoryItemId) {
      errors.inventoryItemId = t('common.errors.required');
    }

    if (formData.type === 'purchase' && formData.quantity <= 0) {
      errors.quantity = t('common.errors.positiveNumber');
    }

    if ((formData.type === 'consumption' || formData.type === 'sale') && formData.quantity >= 0) {
      errors.quantity = t('common.errors.negativeNumber');
    }

    if (formData.type === 'purchase' && formData.costPrice !== undefined && formData.costPrice < 0) {
      errors.costPrice = t('common.errors.positiveNumber');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    // Convert numeric inputs to numbers
    if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? '' : Number(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      // Create new stock movement
      await actions.inventory.addStockMovement(formData);
      toast.success(t('admin.inventory.movementEditor.movementCreatedSuccess'));

      // Redirect to stock movements list
      history.push(R.StockMovementsList);
    } catch (error) {
      console.error('Error saving stock movement:', error);
      toast.error(t('admin.inventory.movementEditor.errorSavingMovement'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <Link to={R.StockMovementsList} className={styles.backButton}>
            <ArrowLeftTwoTone />
          </Link>
          <h1 className={styles.title}>
            {t('admin.inventory.movementEditor.addMovementTitle')}
          </h1>
        </div>
      </div>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="inventoryItemId">
            {t('admin.inventory.movementEditor.itemLabel')}
          </label>
          <select
            id="inventoryItemId"
            name="inventoryItemId"
            className={styles.select}
            value={formData.inventoryItemId}
            onChange={handleChange}
            required
          >
            <option value="">{t('common.chooseAnOption')}</option>
            {activeItems.map(item => (
              <option key={item.id} value={item.id}>
                {item.name} ({parseFloat(item.quantity.toString()).toFixed(2)} {item.unit})
              </option>
            ))}
          </select>
          {formErrors.inventoryItemId && <div className={styles.error}>{formErrors.inventoryItemId}</div>}

          {selectedItem && (
            <div className={styles.info}>
              <span className={styles.infoLabel}>{t('admin.inventory.items.currentStock')}:</span>
              {parseFloat(selectedItem.quantity.toString()).toFixed(2)} {selectedItem.unit}
            </div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="type">
            {t('admin.inventory.movementEditor.typeLabel')}
          </label>
          <select
            id="type"
            name="type"
            className={styles.select}
            value={formData.type}
            onChange={handleChange}
          >
            <option value="purchase">{t('admin.inventory.movements.purchase')}</option>
            <option value="consumption">{t('admin.inventory.movements.consumption')}</option>
            <option value="sale">{t('admin.inventory.movements.sale')}</option>
            <option value="adjustment">{t('admin.inventory.movements.adjustment')}</option>
          </select>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="quantity">
            {t('admin.inventory.movementEditor.quantityLabel')}
            {formData.type === 'purchase' && ` (${t('common.positive')})`}
            {(formData.type === 'consumption' || formData.type === 'sale') && ` (${t('common.negative')})`}
          </label>
          <input
            id="quantity"
            name="quantity"
            type="number"
            step="0.01"
            className={styles.input}
            value={formData.quantity}
            onChange={handleChange}
            required
          />
          {formErrors.quantity && <div className={styles.error}>{formErrors.quantity}</div>}
        </div>

        {formData.type === 'purchase' && (
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="costPrice">
              {t('admin.inventory.movementEditor.costPriceLabel')}
            </label>
            <input
              id="costPrice"
              name="costPrice"
              type="number"
              step="0.01"
              className={styles.input}
              value={formData.costPrice}
              onChange={handleChange}
              required
            />
            {formErrors.costPrice && <div className={styles.error}>{formErrors.costPrice}</div>}
          </div>
        )}

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="reference">
            {t('admin.inventory.movementEditor.referenceLabel')}
          </label>
          <input
            id="reference"
            name="reference"
            type="text"
            className={styles.input}
            value={formData.reference}
            onChange={handleChange}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="notes">
            {t('admin.inventory.movementEditor.notesLabel')}
          </label>
          <textarea
            id="notes"
            name="notes"
            className={styles.textarea}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>

        <div className={styles.buttonGroup}>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? t('common.saving') : t('common.create')}
          </button>

          <Link to={R.StockMovementsList} className={styles.cancelButton}>
            {t('common.cancel')}
          </Link>
        </div>
      </form>
    </div>
  );
};

export default StockMovementEditor;
